import { useMemo } from "react"
import { DateTime } from "luxon"
import {
  ResponsiveContainer,
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts"
import DateRangeControl from "../../components/DateRangeControl"
import {
  useDateRangeFilter,
  CUSTOM_RANGE,
} from "../../hooks/useDateRangeFilter"
import { useEventQrScanVolume } from "../../hooks/useEventQr"
import { formatRangeLabel } from "../../utils/datetime"

const BLUE = "#2563eb"
const DEFAULT_RANGE = "This Week"
const EVENT_DATES = "Event dates"
// Beyond this many points the dots crowd the line, so only the line shows.
const MAX_DOTTED_POINTS = 31

/*
  The event's own dates as a filter option, in the viewer's time zone:
  StartsAt (or the day the QR was created, since it was live from then)
  through ExpiresAt (or today, while it has no expiry). A future start with
  no expiry would end before it begins, so the end never precedes the start.
*/
const getEventDatesRange = (event) => {
  const start = DateTime.fromISO(event.startsAt || event.createdAt)
  const expiry = event.expiresAt ? DateTime.fromISO(event.expiresAt) : null
  const end = DateTime.max(expiry || DateTime.now(), start)

  const startDate = start.toISODate()
  const endDate = end.toISODate()

  return {
    value: EVENT_DATES,
    label: `${EVENT_DATES} (${formatRangeLabel(startDate, endDate)})`,
    start: startDate,
    end: endDate,
  }
}

/*
  Buckets the API's UTC hours into the viewer's local hours (a single-day
  range) or local days (anything longer), with every empty bucket filled in.
*/
const buildSeries = (hours, start, end, isHourly) => {
  const unit = isHourly ? "hour" : "day"
  const keyOf = (dateTime) =>
    isHourly ? dateTime.toFormat("yyyy-MM-dd'T'HH") : dateTime.toISODate()

  const counts = new Map()
  hours.forEach(({ hour, scans }) => {
    const key = keyOf(DateTime.fromISO(hour).toLocal())
    counts.set(key, (counts.get(key) || 0) + scans)
  })

  const series = []
  let cursor = DateTime.fromISO(start).startOf("day")
  const last = DateTime.fromISO(end).endOf("day")
  while (cursor <= last) {
    series.push({ at: cursor.toISO(), scans: counts.get(keyOf(cursor)) || 0 })
    cursor = cursor.plus({ [`${unit}s`]: 1 })
  }

  return series
}

const EventScansChart = ({ event }) => {
  const eventDatesRange = useMemo(() => getEventDatesRange(event), [event])

  const {
    dateRange,
    start,
    end,
    rangeLabel,
    triggerLabel,
    presetOptions,
    getCustomSeed,
    handleSelectPreset,
    handleApplyCustom,
  } = useDateRangeFilter({
    defaultRange: DEFAULT_RANGE,
    extraRanges: [eventDatesRange],
  })

  const isHourly = !!start && start === end

  // Local midnight to the next local midnight, sent as UTC instants.
  const from = start
    ? DateTime.fromISO(start).startOf("day").toUTC().toISO()
    : ""
  const to = end
    ? DateTime.fromISO(end).plus({ days: 1 }).startOf("day").toUTC().toISO()
    : ""

  const {
    data: hours = [],
    isLoading,
    isFetching,
  } = useEventQrScanVolume({
    id: event.id,
    from,
    to,
  })

  const series = useMemo(
    () => (start && end ? buildSeries(hours, start, end, isHourly) : []),
    [hours, start, end, isHourly]
  )
  const total = series.reduce((sum, point) => sum + point.scans, 0)

  const tickFormat = isHourly ? "h a" : "M/d"
  const tooltipFormat = isHourly ? "MMM d, h a" : "ccc, MMM d"

  return (
    <section className="rounded-2xl border border-gray-200 bg-white p-4 shadow-sm">
      <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
        <div>
          <h2 className="text-sm font-semibold text-gray-900">Scans</h2>
          <p className="text-xs text-gray-500">
            {rangeLabel || "Pick a range"} · {isHourly ? "hourly" : "daily"}
            {total > 0 && ` · ${total.toLocaleString()} total`}
            {isFetching && !isLoading && " · updating…"}
          </p>
        </div>
        <DateRangeControl
          customLabel={CUSTOM_RANGE}
          getCustomSeed={getCustomSeed}
          presets={presetOptions}
          triggerLabel={triggerLabel}
          value={dateRange}
          onApplyCustom={handleApplyCustom}
          onSelectPreset={handleSelectPreset}
        />
      </div>

      {isLoading ? (
        <div className="h-[240px] animate-pulse rounded-xl bg-gray-50" />
      ) : total === 0 ? (
        <p className="flex h-[240px] items-center justify-center text-sm text-gray-400">
          No scans in this period.
        </p>
      ) : (
        <ResponsiveContainer height={240} width="100%">
          <AreaChart
            data={series}
            margin={{ top: 8, right: 16, bottom: 4, left: 4 }}
          >
            <defs>
              <linearGradient id="eventScansFill" x1="0" x2="0" y1="0" y2="1">
                <stop offset="0%" stopColor={BLUE} stopOpacity={0.25} />
                <stop offset="100%" stopColor={BLUE} stopOpacity={0} />
              </linearGradient>
            </defs>
            <CartesianGrid stroke="#e5e7eb" vertical={false} />
            <XAxis
              axisLine={false}
              dataKey="at"
              minTickGap={16}
              stroke="#9ca3af"
              tick={{ fontSize: 11 }}
              tickFormatter={(iso) =>
                DateTime.fromISO(iso).toFormat(tickFormat)
              }
              tickLine={false}
            />
            <YAxis
              allowDecimals={false}
              axisLine={false}
              stroke="#9ca3af"
              tick={{ fontSize: 11 }}
              tickLine={false}
              width={32}
            />
            <Tooltip
              formatter={(value) => [value, "Scans"]}
              labelFormatter={(iso) =>
                DateTime.fromISO(iso).toFormat(tooltipFormat)
              }
            />
            <Area
              dataKey="scans"
              dot={
                series.length <= MAX_DOTTED_POINTS
                  ? { r: 3, fill: BLUE, strokeWidth: 0 }
                  : false
              }
              fill="url(#eventScansFill)"
              stroke={BLUE}
              strokeWidth={2}
              type="monotone"
            />
          </AreaChart>
        </ResponsiveContainer>
      )}
    </section>
  )
}

export default EventScansChart
